"use client";
import { useEffect, useRef } from "react";
import { FaMailchimp } from "react-icons/fa";
import cn from "@/utils/cn";

export type MailchimpConnectMode = "audit" | "integration" | "auth";

export interface IntegrationSuccessPayload {
  keyId: string;
  skipRefresh: boolean;
}

export interface AuthSuccessPayload {
  loginToken: string;
  isNewUser: boolean;
}

interface MailchimpConnectButtonProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  withLabel?: boolean;
  label?: string;
  disabled?: boolean;
  /**
   * Popup mode. When set, OAuth runs in a popup window and the result is
   * delivered via the appropriate callback below.
   *   - "audit":       no account created; token returned via onTokenReceived
   *   - "integration": logged-in user links Mailchimp; keyId via onIntegrationSuccess
   *   - "auth":        anonymous sign-up; loginToken via onAuthSuccess
   * If `mode` is omitted, falls back to a full-page redirect to /api/mailchimp/auth.
   */
  mode?: MailchimpConnectMode;
  onTokenReceived?: (token: string) => void;
  onIntegrationSuccess?: (payload: IntegrationSuccessPayload) => void;
  onAuthSuccess?: (payload: AuthSuccessPayload) => void;
  onError?: (errorCode: string) => void;
}

const POLL_INTERVAL_MS = 800;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

function makeStateId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const CONTEXT_PARAM: Record<MailchimpConnectMode, string> = {
  audit: "audit",
  integration: "integration_popup",
  auth: "auth_popup",
};

export default function MailchimpConnectButton({
  className,
  size = "md",
  withLabel = false,
  label = "Connect Mailchimp Account",
  disabled = false,
  mode,
  onTokenReceived,
  onIntegrationSuccess,
  onAuthSuccess,
  onError,
}: MailchimpConnectButtonProps) {
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs mirror the latest callbacks so the polling closure always invokes
  // the freshest version (avoids stale closures over parent state).
  const onTokenReceivedRef = useRef(onTokenReceived);
  const onIntegrationSuccessRef = useRef(onIntegrationSuccess);
  const onAuthSuccessRef = useRef(onAuthSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onTokenReceivedRef.current = onTokenReceived;
    onIntegrationSuccessRef.current = onIntegrationSuccess;
    onAuthSuccessRef.current = onAuthSuccess;
    onErrorRef.current = onError;
  }, [onTokenReceived, onIntegrationSuccess, onAuthSuccess, onError]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const beginPolling = (stateId: string) => {
    stopPolling();
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const res = await fetch(`/api/mailchimp/audit-token/${stateId}`, { cache: "no-store" });
        if (res.status === 404) return;
        if (!res.ok) return;
        const data = await res.json();
        if (!data?.ready) return;

        const closePopup = () => {
          try { popupRef.current?.close(); } catch {}
        };

        switch (data.kind) {
          case "audit_token":
            if (typeof data.token === "string") {
              stopPolling();
              closePopup();
              onTokenReceivedRef.current?.(data.token);
            }
            break;
          case "integration_success":
            if (typeof data.keyId === "string") {
              stopPolling();
              closePopup();
              onIntegrationSuccessRef.current?.({
                keyId: data.keyId,
                skipRefresh: !!data.skipRefresh,
              });
            }
            break;
          case "auth_success":
            if (typeof data.loginToken === "string") {
              stopPolling();
              closePopup();
              onAuthSuccessRef.current?.({
                loginToken: data.loginToken,
                isNewUser: !!data.isNewUser,
              });
            }
            break;
          case "error":
            stopPolling();
            closePopup();
            onErrorRef.current?.(typeof data.error === "string" ? data.error : "unknown_error");
            break;
        }
      } catch {
        // Individual poll failures are non-fatal; the timeout handles the
        // case where the popup never returns.
      }
    };

    pollTimerRef.current = setInterval(() => {
      tick();
      if (popupRef.current && popupRef.current.closed) {
        if (Date.now() - startedAt > 4000) {
          stopPolling();
          onErrorRef.current?.("popup_closed");
        }
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      stopPolling();
      onErrorRef.current?.("timeout");
    }, POLL_TIMEOUT_MS);

    tick();
  };

  const handleConnect = () => {
    if (disabled) return;

    if (mode) {
      const stateId = makeStateId();
      const width = 600;
      const height = 720;
      const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
      const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
      const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no`;
      const popup = window.open(
        `/api/mailchimp/auth?context=${CONTEXT_PARAM[mode]}&popup_state=${encodeURIComponent(stateId)}`,
        "mailchimpOAuth",
        features,
      );
      if (!popup) {
        onErrorRef.current?.("popup_blocked");
        return;
      }
      popupRef.current = popup;
      popup.focus?.();
      beginPolling(stateId);
      return;
    }

    // No mode set: legacy full-page redirect.
    window.location.href = "/api/mailchimp/auth";
  };

  const sizeClasses = {
    sm: "size-8 text-lg",
    md: "size-10 text-xl",
    lg: "size-12 text-2xl",
  };

  if (withLabel) {
    return (
      <button
        type="button"
        onClick={handleConnect}
        disabled={disabled}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl border border-kd-secondary-6 bg-[#FAFBFC] px-4 py-2 text-sm font-medium text-kd-secondary-2 shadow-sm transition-colors",
          "hover:border-kd-lte-blue hover:bg-white",
          "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-kd-secondary-6 disabled:hover:bg-[#FAFBFC]",
          className,
        )}
        title="Connect with Mailchimp Account"
        aria-label="Connect with Mailchimp Account"
      >
        <span className="flex size-6 items-center justify-center rounded-full bg-[#FFDD22]">
          <FaMailchimp className="size-4 text-black" />
        </span>
        <span className="whitespace-nowrap">{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleConnect}
      disabled={disabled}
      className={cn(
        "flex items-center justify-center rounded-xl bg-[#FAFBFC] text-kd-secondary-2 border border-kd-secondary-6 hover:bg-white hover:border-kd-lte-blue transition-colors shadow-sm",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#FAFBFC] disabled:hover:border-kd-secondary-6",
        sizeClasses[size],
        className,
      )}
      title="Connect with Mailchimp Account"
      aria-label="Connect with Mailchimp"
    >
      <FaMailchimp />
    </button>
  );
}
